import { useState, useMemo, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { X, Layout, Type, AlignLeft, Tag, FileText, Activity, Signal, User, UserCheck, Calendar, GitFork, Hash, Users } from 'lucide-react'
import { Modal } from '../../../shared/components/ui/Modal'
import { Button } from '../../../shared/components/ui/Button'
import { Input } from '../../../shared/components/ui/Input'
import { Select, type SelectOption } from '../../../shared/components/ui/Select'
import { useIssues, useStatuses, useIssueMutations } from '../hooks'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import { useUser } from '../../../shared/hooks/useAuth'
import type { Issue } from '../api'
import { canonicalDepartmentName } from '@jira-clone/shared-types'
import { ISSUE_TYPE_OPTIONS, PRIORITY_OPTIONS } from '../boardUtils'

export interface NewIssueModalProps {
  open: boolean
  onClose: () => void
  spaceId: string
  initialStatus?: { id: string; order?: number }
}

const LABEL_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
]

function getLabelTone(val: string): string {
  let hash = 0
  for (let i = 0; i < val.length; i++) hash = (hash * 31 + val.charCodeAt(i)) >>> 0
  return LABEL_COLORS[hash % LABEL_COLORS.length]
}

const DEPARTMENT_OPTIONS = ['Development', 'Creative & Marketing']

function getDepartmentTeamName(dept?: string | null): string {
  if (!dept) return 'Development'
  const d = dept.trim()
  if (/marketing/i.test(d)) return 'Creative & Marketing'
  if (/develop/i.test(d) || /engineer/i.test(d)) return 'Development'
  if (/creat/i.test(d) || /design/i.test(d)) return 'Creative & Marketing'
  if (/sales/i.test(d)) return 'Sales'
  if (/hr/i.test(d) || /human/i.test(d)) return 'HR'
  if (/account/i.test(d) || /finance/i.test(d)) return 'Accounts'
  if (/superadmin/i.test(d) || /admin/i.test(d)) return 'Development'
  return d
}

function matchesDepartment(
  user: { department?: string | null; role?: string | null },
  targetDept: string,
): boolean {
  if (!targetDept) return true
  const role = (user.role || '').toLowerCase()
  if (role === 'superadmin' || role === 'owner') return true

  const uDept = String(canonicalDepartmentName(user.department)).toLowerCase()
  const tDept = canonicalDepartmentName(targetDept).toLowerCase()

  if (tDept.includes('creat') || tDept.includes('design')) {
    return uDept.includes('creat') || uDept.includes('design')
  }
  if (tDept.includes('develop') || tDept.includes('tech') || tDept.includes('engineer')) {
    return uDept.includes('develop') || uDept.includes('tech') || uDept.includes('engineer')
  }
  return uDept.includes(tDept) || tDept.includes(uDept)
}

export function NewIssueModal({ open, onClose, spaceId, initialStatus }: NewIssueModalProps) {
  const { data: issues = [] } = useIssues(spaceId)
  const { data: statuses = [] } = useStatuses(spaceId)
  const { data: members = [] } = useUserOptions(spaceId)
  const { data: allAssignableUsers = [] } = useUserOptions(null)
  const currentUser = useUser()
  const { create, update } = useIssueMutations(spaceId)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<Issue['type']>('task')
  const [priority, setPriority] = useState<Issue['priority']>('medium')
  const [statusId, setStatusId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [reporterId, setReporterId] = useState('')
  const [parentId, setParentId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [storyPoints, setStoryPoints] = useState('')
  const [labels, setLabels] = useState<string[]>([])
  const [labelDraft, setLabelDraft] = useState('')
  const [createAnother, setCreateAnother] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isSuperAdmin = useMemo(() => {
    const dept = (currentUser?.department || '').toLowerCase()
    const role = (currentUser?.role || '').toLowerCase()
    return dept === 'superadmin' || role === 'superadmin' || role === 'owner'
  }, [currentUser])

  const userDepartment = useMemo(() => {
    return getDepartmentTeamName(currentUser?.department)
  }, [currentUser])

  const [team, setTeam] = useState('Development')

  useEffect(() => {
    if (userDepartment) {
      setTeam(userDepartment)
    }
  }, [userDepartment])

  const activeDepartment = useMemo(() => {
    return isSuperAdmin ? getDepartmentTeamName(team) : userDepartment
  }, [isSuperAdmin, team, userDepartment])

  const defaultStatusId = initialStatus?.id ?? statuses.find((s) => s.category === 'todo')?.id ?? statuses[0]?.id ?? ''
  const effectiveStatusId = statusId || defaultStatusId

  const parentCandidates = useMemo(
    () => (issues as Issue[]).filter((i) => (type === 'subtask' ? i.type !== 'subtask' : i.type === 'epic')),
    [issues, type],
  )

  const uniqueStatuses = useMemo(() => {
    const seen = new Set<string>()
    return statuses.filter((st) => {
      const key = st.name.toLowerCase().trim()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [statuses])

  const uniqueMembers = useMemo(() => {
    const seen = new Set<string>()
    const pool = [
      ...(members || []),
      ...(allAssignableUsers || []),
    ]
    return pool
      .map((m: any) => ({
        id: m.userId || m.id,
        display_name: m.display_name || m.name || m.email || 'Member',
        department: m.department || m.dept || m.user?.department || '',
        role: m.role || m.user?.role || '',
      }))
      .filter((m) => {
        if (!m.id) return false
        if (seen.has(m.id)) return false
        seen.add(m.id)
        return true
      })
  }, [members, allAssignableUsers])

  const filteredMembers = useMemo(() => {
    const list = uniqueMembers.filter((m) => matchesDepartment(m, activeDepartment))
    return list.length > 0 ? list : uniqueMembers
  }, [uniqueMembers, activeDepartment])

  const statusOptions = uniqueStatuses.map((s) => ({ value: s.id, label: s.name }))

  // Assignee: lists role assignees of the department service who are selected for the project
  const memberOptions: SelectOption[] = [
    { value: '', label: 'Unassigned' },
    ...filteredMembers.map((m) => ({ value: m.id, label: m.display_name })),
  ]

  // Reporter: any assignable user.
  const reporterOptions: SelectOption[] = useMemo(
    () => uniqueMembers.map((m) => ({ value: m.id, label: m.display_name })),
    [uniqueMembers]
  )
  const effectiveReporterId = reporterId || uniqueMembers[0]?.id || ''

  const parentOptions = [
    { value: '', label: type === 'subtask' ? 'No parent' : 'No epic' },
    ...parentCandidates.map((i) => ({ value: i.id, label: `${i.key} — ${i.title}` })),
  ]

  const reset = () => {
    setTitle('')
    setDescription('')
    setType('task')
    setPriority('medium')
    setStatusId('')
    setAssigneeId('')
    setReporterId('')
    setParentId('')
    setDueDate('')
    setStoryPoints('')
    setLabels([])
    setLabelDraft('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Summary is required.')
      return
    }
    setIsSubmitting(true)
    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        type,
        priority,
        description: description.trim() || undefined,
        assignee_id: assigneeId || null,
        reporter_id: effectiveReporterId || null,
        parent_id: parentId || null,
        due_date: dueDate || null,
        status: effectiveStatusId,
        tags: labels,
        team: activeDepartment,
        customFieldValues: { team: activeDepartment },
        estimatedHours: storyPoints ? Number(storyPoints) : undefined,
      })

      if (effectiveStatusId && created.status !== effectiveStatusId) {
        await update.mutateAsync({
          id: created.id,
          payload: { status: effectiveStatusId },
        })
      }

      toast.success(`${created.key ?? 'Issue'} created.`)
      if (createAnother) {
        reset()
      } else {
        handleClose()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not create issue.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2 text-primary">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Layout className="h-5 w-5" />
          </div>
          <span className="text-lg font-bold">Create Issue</span>
        </div>
      }
      size="2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
            <input
              type="checkbox"
              checked={createAnother}
              onChange={(e) => setCreateAnother(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/30"
            />
            Create another issue
          </label>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose} className="rounded-full px-6">
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting || create.isPending} className="rounded-full px-6 shadow-md shadow-primary/20">
              Create Issue
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col md:flex-row gap-6 lg:gap-8">
        {/* Main Content (Left Column) */}
        <div className="flex-1 space-y-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Type className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                Summary <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="What needs to be done?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                className="text-base font-medium h-11"
              />
            </div>

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <AlignLeft className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                Description
              </label>
              <textarea
                className="min-h-[220px] w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                placeholder="Add context, details, and acceptance criteria..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            
            <div className="space-y-1.5 pt-2">
              <label className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Tag className="h-4 w-4 text-pink-500 dark:text-pink-400" />
                Labels
              </label>
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-input bg-transparent p-2 min-h-[2.75rem] shadow-sm">
                {labels.map((label) => (
                  <span
                    key={label}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm ${getLabelTone(label)}`}
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => setLabels((ls) => ls.filter((l) => l !== label))}
                      aria-label={`Remove label ${label}`}
                      className="hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  className="h-7 min-w-[120px] flex-1 border-none bg-transparent text-sm focus:outline-none focus:ring-0 px-2"
                  placeholder={labels.length === 0 ? "Type and press Enter to add labels..." : "Add more labels..."}
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    const next = labelDraft.trim()
                    if (next && !labels.includes(next)) setLabels((ls) => [...ls, next])
                    setLabelDraft('')
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Metadata (Right Column) */}
        <div className="md:w-80 lg:w-[420px] shrink-0 space-y-5 rounded-xl bg-muted/40 p-5 border border-border/50">
          <div className="space-y-4">
            
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <FileText className="h-3.5 w-3.5 text-orange-500 dark:text-orange-400" />
                Issue Type
              </label>
              <Select
                options={ISSUE_TYPE_OPTIONS}
                value={type}
                onValueChange={(v) => {
                  setType(v as Issue['type'])
                  setParentId('')
                }}
              />
            </div>

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Users className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
                Team
              </label>
              {isSuperAdmin ? (
                <Select
                  options={DEPARTMENT_OPTIONS.map((d) => ({ value: d, label: d }))}
                  value={activeDepartment}
                  onValueChange={(v) => setTeam(v)}
                />
              ) : (
                <div className="flex h-9 w-full items-center rounded-md border border-input bg-background/50 px-3 py-1 text-sm shadow-xs font-medium text-foreground">
                  <span
                    className={`mr-2 h-2 w-2 rounded-full ${
                      activeDepartment === 'Creative & Marketing'
                        ? 'bg-pink-500'
                        : 'bg-indigo-500'
                    }`}
                  />
                  {activeDepartment}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Activity className="h-3.5 w-3.5 text-sky-500 dark:text-sky-400" />
                Status
              </label>
              <Select
                options={statusOptions}
                value={effectiveStatusId}
                onValueChange={setStatusId}
              />
            </div>

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Signal className="h-3.5 w-3.5 text-rose-500 dark:text-rose-400" />
                Priority
              </label>
              <Select
                options={PRIORITY_OPTIONS}
                value={priority}
                onValueChange={(v) => setPriority(v as Issue['priority'])}
              />
            </div>

            <hr className="border-border/60" />

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <User className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                Assignee
              </label>
              <Select
                options={memberOptions}
                value={assigneeId}
                onValueChange={setAssigneeId}
              />
            </div>

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <UserCheck className="h-3.5 w-3.5 text-teal-500 dark:text-teal-400" />
                Reporter
              </label>
              <Select
                options={reporterOptions}
                value={effectiveReporterId}
                onValueChange={setReporterId}
              />
            </div>

            <hr className="border-border/60" />

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Calendar className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                Due Date
              </label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            {type !== 'epic' && (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <GitFork className="h-3.5 w-3.5 text-violet-500 dark:text-violet-400" />
                  {type === 'subtask' ? 'Parent Issue' : 'Epic / Parent'}
                </label>
                <Select
                  options={parentOptions}
                  value={parentId}
                  onValueChange={setParentId}
                />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Hash className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" />
                Story Points
              </label>
              <Input
                type="number"
                min={0}
                placeholder="Estimate"
                value={storyPoints}
                onChange={(e) => setStoryPoints(e.target.value)}
              />
            </div>
            
          </div>
        </div>
      </div>
    </Modal>
  )
}
